import React, { useState, useEffect, useContext, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import { withOktaAuth } from "@okta/okta-react";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Typography from "@material-ui/core/Typography";
import styles from "./styles";
import { Grid } from "@material-ui/core";
import InputLabel from "@material-ui/core/InputLabel";
import { httpPost } from "../../../../API/Http";
import Chip from "@material-ui/core/Chip";
import { withApollo } from "react-apollo";
import LoadingIndicator from "../../../../Component/LoadingIndicator";
import ModalDialogContext from "../../../../Context/ModalDialog/ModalDialogContext";
import { getCountryApprovers } from "../../../../API/Country";
import { getUserFinalizeComment } from "../../../../API/Entity";
import SearchableSelect from "../../../../Component/Forms/SearchableSelect";
import TextField from "../../../../Component/Forms/TextField";
import TextArea from "../../../../Component/Forms/TextArea";
import { useTranslation } from "react-i18next";
import CheckBox from "../../../../Component/Forms/CheckboxWithLabel";
import CustomButton from "../../../../Component/CustomButton";

const useStyles = makeStyles(styles);

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const ApproveRequest = ({ open, scoreData, checkRequest, entity, onClose }) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(open);
  const [guidedScore, setGuidedScore] = useState("");
  const [acceptedScore, setAcceptedScore] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setCreating] = useState(false);
  const [approverComment, setApproverComment] = useState("");
  const [userComment, setUserComment] = useState("");
  const [uploadedFiles, setUploadedFile] = useState([]);
  const [approverList, setApproverList] = useState();
  const [approvedBy, setApprovedBy] = useState("");
  const [proceedAtRisk, setProceedAtRisk] = useState(false);

  const { showMessage, showErrorMessage } = useContext(ModalDialogContext);
  const { t } = useTranslation();

  useEffect(() => {
    setIsOpen(open);
    (async function loader() {
      await getAllApprovers();
      await getFinalizeComment();
      await formatScores();
      setProceedAtRisk(
        checkRequest.process_flow_state === "Escalated Proceed At Risk",
      );
    })();
  }, [open]);

  const onDrop = useCallback((acceptedFiles) => {
    let data = [...uploadedFiles];
    acceptedFiles.forEach((file) => {
      data.push(file);
    });
    setUploadedFile(data);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const formatScores = () => {
    let guided =
      scoreData && checkRequest ? scoreData[checkRequest.check_type] : "";

    setGuidedScore(guided.guided_score);
    setAcceptedScore(guided.accepted_score);
    setIsLoading(false);
  };

  const getAllApprovers = async () => {
    try {
      const result = await getCountryApprovers();
      const { data = [] } = result;
      setApproverList(
        data.map((c) => {
          return { label: c.label, value: c.value };
        }),
      );
    } catch (error) {
      showErrorMessage({ error });
    }
  };

  const getFinalizeComment = async () => {
    try {
      let scoreId = 0;
      if (scoreData) {
        scoreId = scoreData[checkRequest.check_type].id;
      }
      const result = await getUserFinalizeComment(scoreId);
      const { data = {} } = result;
      const { comments = "" } = data;
      setUserComment(comments);
    } catch (error) {
      showErrorMessage({ error });
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    onClose("cancel");
  };

  const handleSubmit = async (action) => {
    try {
      setIsLoading(true);
      setCreating(true);

      let scoreId = 0;

      if (scoreData) {
        scoreId = Number(scoreData[checkRequest.check_type].id);
      }

      const formData = new FormData();
      formData.append("checkType", checkRequest.check_type);
      formData.append("entityId", entity.id);
      formData.append("action", action);
      formData.append("scoreId", scoreId);
      formData.append("approverComment", approverComment);
      formData.append("approvedBy", JSON.stringify(approvedBy));
      formData.append("proceedAtRisk", proceedAtRisk);
      for (let i = 0; i < uploadedFiles.length; i++) {
        formData.append(`data`, uploadedFiles[i]);
      }

      await httpPost("/entity/approveEntityScore", formData);
      showMessage({ message: t("messages.check.finalization.approverAction") });
      setIsOpen(false);
      setUploadedFile([]);
      onClose();
    } catch (error) {
      showErrorMessage({ error });
    }
    setIsLoading(false);
    setCreating(false);
  };

  const updateApproverComment = (v) => {
    setApproverComment(v);
  };

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  const renderFinalizeForm = () => {
    return (
      <>
        <Grid container direction="row" spacing={4}>
          <Grid item xs={12} md={6} sm={6} style={{ paddingLeft: 15 }}>
            <TextField
              label={t("labels.check.finalization.guidedScore")}
              initialValue={guidedScore}
              type="text"
              disabled={true}
            />
          </Grid>
          <Grid item sm={6} md={6} xs={12}>
            <TextField
              label={t("labels.check.finalization.userScore")}
              initialValue={acceptedScore}
              type="text"
              disabled={true}
            />
          </Grid>
        </Grid>
        <Grid container direction="row" spacing={4}>
          <Grid item xs={12} md={6} sm={12} style={{ paddingLeft: 15 }}>
            <TextArea
              label={t("labels.check.finalization.userComment")}
              rowsMax={3}
              placeholder={t("labels.check.finalization.pleaseEnterYouComment")}
              initialValue={userComment}
              disabled={true}
            />
          </Grid>
          <Grid item xs={12} md={6} sm={12} style={{ paddingLeft: 15 }}>
            <TextArea
              label={t("labels.check.finalization.approverComment")}
              rowsMax={3}
              placeholder={t("labels.check.finalization.pleaseEnterYouComment")}
              initialValue={approverComment}
              onChange={updateApproverComment}
              disabled={false}
            />
          </Grid>
        </Grid>
        <Grid container direction="row" spacing={4}>
          <Grid item xs={12} md={6} sm={12} style={{ paddingLeft: 15 }}>
            <SearchableSelect
              label={t("labels.check.finalization.approvedBy")}
              options={approverList}
              value={approvedBy}
              changeHandler={setApprovedBy}
            />
          </Grid>
          {checkRequest.check_type === "AML" && (
            <Grid container item xs={12} md={6} sm={12}>
              <Grid xs={12} md={12} sm={12}>
                &nbsp;
              </Grid>
              <Grid item xs={12} md={12} sm={12}>
                <CheckBox
                  label={t("labels.check.finalization.proceedAtRisk")}
                  onChange={(e) => {
                    setProceedAtRisk(e.target.checked);
                  }}
                  value={proceedAtRisk}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid container direction="row" spacing={4}>
          <Grid item xs={12} md={12} sm={12} style={{ paddingLeft: 15 }}>
            <InputLabel className={classes.inputLabel}>
              {t("labels.check.finalization.dropFiles")}
            </InputLabel>
            <div className={classes.dropZone}>
              <div {...getRootProps()} className={classes.dropZoneInner}>
                <input {...getInputProps()} />
                <div className={[classes.centerAll]}>
                  <p>{t("labels.check.finalization.dragFiveFiles")}</p>
                </div>
              </div>
            </div>
            {uploadedFiles.map((val, index) => {
              return (
                <Chip
                  label={val.name}
                  key={index}
                  // onDelete={removeFile(val, index)}
                />
              );
            })}
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <Dialog
      disableBackdropClick
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
      fullWidth={true}
      maxWidth="md"
    >
      <DialogTitle id="customized-dialog-title" onClose={handleClose}>
        <Typography className={classes.panelTitle}>
          {t("labels.check.finalization.approveScore")}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? renderLoadingIndicator() : renderFinalizeForm()}
      </DialogContent>
      <DialogActions>
        <CustomButton variant="secondary" isDisabled={isCreating} onClick={handleClose}>
          {t("labels.common.cancel")}
        </CustomButton>
        <CustomButton variant="secondary" isDisabled={isCreating} onClick={() => handleSubmit("Send Back Score")}>
          {t("labels.check.finalization.sendBack")}
        </CustomButton>
        <CustomButton variant="secondary" isDisabled={isCreating} onClick={() => handleSubmit("Reject Score")}>
          {t("labels.check.finalization.reject")}
        </CustomButton>
        <CustomButton variant="primary" isDisabled={isCreating} onClick={() => handleSubmit("Approve Score")}>
          {t("labels.check.finalization.approve")}
        </CustomButton>

        
      </DialogActions>
    </Dialog>
  );
};

export default withOktaAuth(withApollo(ApproveRequest));
